<template>
  <div class="forgot-password" v-if="forgotPasswordShow">
    <!-- Step 1: 忘记密码 - 邮箱 + 验证码 -->
    <el-form ref="forgotForm" :model="forgotForm" :rules="forgotRules" class="forgot-form" v-show="step === 1">
      <i class="el-icon-circle-close close" @click="closeForgotPassword"></i>
      <div class="login-title">
        <span class="logo-img"></span>
        <h1 class="logo-title" style="color: #000;">{{ $t('login.forgotPasswordTitle') }}</h1>
      </div>
      <el-form-item prop="email">
        <el-input
          v-model="forgotForm.email"
          type="text"
          auto-complete="off"
          :placeholder="$t('login.emailPlaceholder')"
        >
          <svg-icon slot="prefix" icon-class="email" class="el-input__icon input-icon" />
        </el-input>
      </el-form-item>
      <el-form-item prop="code">
        <el-input
          v-model="forgotForm.code"
          auto-complete="off"
          :placeholder="$t('login.codePlaceholder')"
          style="width: 63%"
          @keyup.enter.native="handleNext"
        >
          <svg-icon slot="prefix" icon-class="validCode" class="el-input__icon input-icon" />
        </el-input>
        <div class="login-code">
          <el-button
            class="send-code-btn"
            type="primary"
            :disabled="countdown > 0"
            @click="sendCode"
            size="medium"
          >
            <span v-if="countdown === 0">{{ $t('login.sendCode') }}</span>
            <span v-else>{{ countdown }}s {{ $t('login.resendCode') }}</span>
          </el-button>
        </div>
      </el-form-item>
      <el-form-item style="width:100%;">
        <el-button
          :loading="loading"
          size="medium"
          type="primary"
          style="width:100%;"
          @click.native.prevent="handleNext"
        >
          <span v-if="!loading">{{ $t('login.next') }}</span>
          <span v-else>{{ $t('login.next') }}...</span>
        </el-button>
        <a class="back-to-login-link" @click="handleBackToLogin">{{ $t('login.backToLogin') }}</a>
      </el-form-item>
    </el-form>

    <!-- Step 2: 重置密码 - 新密码 + 确认密码 -->
    <el-form ref="resetForm" :model="resetForm" :rules="resetRules" class="forgot-form" v-show="step === 2">
      <i class="el-icon-circle-close close" @click="closeForgotPassword"></i>
      <div class="login-title">
        <span class="logo-img"></span>
        <h1 class="logo-title" style="color: #000;">{{ $t('login.resetPasswordTitle') }}</h1>
      </div>
      <el-form-item prop="newPassword">
        <el-input
          v-model="resetForm.newPassword"
          type="password"
          auto-complete="off"
          :placeholder="$t('login.newPasswordPlaceholder')"
          show-password
          @keyup.enter.native="handleReset"
        >
          <svg-icon slot="prefix" icon-class="password" class="el-input__icon input-icon" />
        </el-input>
      </el-form-item>
      <el-form-item prop="confirmPassword">
        <el-input
          v-model="resetForm.confirmPassword"
          type="password"
          auto-complete="off"
          :placeholder="$t('login.confirmPasswordPlaceholder')"
          show-password
          @keyup.enter.native="handleReset"
        >
          <svg-icon slot="prefix" icon-class="password" class="el-input__icon input-icon" />
        </el-input>
      </el-form-item>
      <el-form-item style="width:100%;">
        <el-button
          :loading="loading"
          size="medium"
          type="primary"
          style="width:100%;"
          @click.native.prevent="handleReset"
        >
          <span v-if="!loading">{{ $t('login.resetPasswordBtn') }}</span>
          <span v-else>{{ $t('login.resetPasswordBtn') }}...</span>
        </el-button>
        <a class="back-to-login-link" @click="handleBackToLogin">{{ $t('login.backToLogin') }}</a>
      </el-form-item>
    </el-form>

    <!-- Step 3: 重置成功 -->
    <div class="forgot-form" v-show="step === 3">
      <i class="el-icon-circle-close close" @click="closeForgotPassword"></i>
      <div class="login-title">
        <span class="logo-img"></span>
        <h1 class="logo-title" style="color: #000;">{{ $t('login.resetSuccessTitle') }}</h1>
      </div>
      <div class="success-content">
        <i class="el-icon-success success-icon"></i>
        <p class="success-message">{{ $t('login.resetSuccessMessage') }}</p>
        <p class="success-tips">{{ $t('login.resetSuccessTips') }}</p>
      </div>
      <el-form-item style="width:100%;">
        <el-button
          size="medium"
          type="primary"
          style="width:100%;"
          @click.native.prevent="handleBackToLogin"
        >
          {{ $t('login.backToLogin') }}
        </el-button>
      </el-form-item>
    </div>
  </div>
</template>

<script>
import { sendResetCode, validateResetCode, resetPassword } from "@/api/user";
import SvgIcon from '@/components/SvgIcon'

export default {
  name: "ForgotPassword",
  components: {
    SvgIcon
  },
  data() {
    return {
      forgotPasswordShow: true,
      step: 1,
      countdown: 0,
      countdownTimer: null,
      loading: false,
      forgotForm: {
        email: '',
        code: ''
      },
      resetForm: {
        newPassword: '',
        confirmPassword: ''
      },
      forgotRules: {
        email: [
          { required: true, trigger: "blur", message: this.$t('login.emailRequired') },
          { validator: this.validateEmail, trigger: 'blur' }
        ],
        code: [
          { required: true, trigger: "change", message: this.$t('login.codeRequired') }
        ]
      },
      resetRules: {
        newPassword: [
          { required: true, trigger: "change", message: this.$t('login.newPasswordRequired') }
        ],
        confirmPassword: [
          { required: true, trigger: "change", message: this.$t('login.confirmPasswordRequired') },
          { validator: this.validatePasswordMatch, trigger: 'blur' }
        ]
      }
    };
  },
  methods: {
    validateEmail(rule, value, callback) {
      if (/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value)) {
        callback();
      } else {
        callback(new Error(this.$t('login.emailInvalid')));
      }
    },
    validatePasswordMatch(rule, value, callback) {
      if (this.resetForm.newPassword === this.resetForm.confirmPassword) {
        callback();
      } else {
        callback(new Error(this.$t('login.passwordMismatch')));
      }
    },
    closeForgotPassword() {
      this.forgotPasswordShow = false;
      this.clearCountdown();
      this.$emit('close');
    },
    clearCountdown() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
      this.countdown = 0;
    },
    sendCode() {
      this.$refs.forgotForm.validateField('email', (errorMessage) => {
        if (errorMessage) {
          return;
        }
        this.loading = true;
        sendResetCode(this.forgotForm.email).then((res) => {
          this.loading = false;
          if (res.code === 200) {
            this.$message({
              message: this.$t('login.sendCodeSuccess'),
              type: 'success',
              offset: 200,
              customClass: 'messageBox'
            });
            // 开始60秒倒计时
            this.countdown = 60;
            this.countdownTimer = setInterval(() => {
              this.countdown--;
              if (this.countdown <= 0) {
                this.clearCountdown();
              }
            }, 1000);
          } else {
            this.$message({
              message: res.msg || res.message || this.$t('login.sendCodeFailed'),
              type: 'error',
              offset: 200,
              customClass: 'messageBox'
            });
          }
        }).catch((error) => {
          this.loading = false;
          this.$message({
            message: error.message || this.$t('login.sendCodeFailed'),
            type: 'error',
            offset: 200,
            customClass: 'messageBox'
          });
        });
      });
    },
    handleNext() {
      this.$refs.forgotForm.validate(valid => {
        if (valid) {
          this.loading = true;
          // 验证验证码
          validateResetCode(this.forgotForm.email, this.forgotForm.code).then((res) => {
            this.loading = false;
            if (res.code === 200) {
              this.step = 2;
            } else {
              this.$message({
                message: res.msg || res.message || this.$t('login.message3'),
                type: 'error',
                offset: 200,
                customClass: 'messageBox'
              });
            }
          }).catch((error) => {
            this.loading = false;
            if (error.message && error.message.includes('验证码')) {
              this.$message({
                message: this.$t('login.message3'),
                type: 'error',
                offset: 200,
                customClass: 'messageBox'
              });
            } else {
              this.$message({
                message: error.message || this.$t('login.message3'),
                type: 'error',
                offset: 200,
                customClass: 'messageBox'
              });
            }
          });
        }
      });
    },
    handleReset() {
      this.$refs.resetForm.validate(valid => {
        if (valid) {
          this.loading = true;
          resetPassword(
            this.forgotForm.email,
            this.forgotForm.code,
            this.resetForm.newPassword
          ).then((res) => {
            this.loading = false;
            if (res.code === 200) {
              this.clearCountdown();
              this.step = 3;
            } else {
              this.$message({
                message: res.msg || res.message || this.$t('login.resetPasswordFailed'),
                type: 'error',
                offset: 200,
                customClass: 'messageBox'
              });
            }
          }).catch((error) => {
            this.loading = false;
            this.$message({
              message: error.message || this.$t('login.resetPasswordFailed'),
              type: 'error',
              offset: 200,
              customClass: 'messageBox'
            });
          });
        }
      });
    },
    handleBackToLogin() {
      this.forgotPasswordShow = false;
      this.clearCountdown();
      this.$emit('backToLogin');
    }
  },
  beforeDestroy() {
    this.clearCountdown();
  }
};
</script>

<style lang="less" scoped>
/deep/ .el-input input {
  border: none;
  border-bottom: 1px solid #000;
  outline: none;
  border-radius: 0;
}
/deep/ .el-input--prefix .el-input__inner {
  padding-left: 15px;
}

.forgot-password {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  position: fixed;
  z-index: 1001 !important;
  top: 0%;
  left: calc(50% - 400px);
  &::before {
    display: block;
    content: '';
    width: 100vw;
    height: 100vh;
    position: fixed;
    z-index: 999 !important;
    top: 0;
    left: 0;
    background-color: rgba(0, 0, 0, 0.5);
  }
  .close {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 10000 !important;
    font-size: 36px;
    color: #000;
  }
}

.title {
  margin: 0px auto 30px auto;
  text-align: center;
  color: #707070;
}

.forgot-form {
  border-radius: 6px;
  background: #ffffff;
  width: 800px;
  z-index: 10000 !important;
  padding: 40px 105px;
  box-sizing: border-box;
  position: relative;
  .login-title {
    width: 100%;
    display: flex;
    flex-direction: column;
    .logo-img {
      width: 100%;
      height: 30px;
      background-image: url("@/assets/image/kams-logo-black.svg");
      background-repeat: no-repeat;
      background-size: 40%;
      background-position: -3rem;
    }
    .logo-title {
      flex: 1;
      display: flex;
      justify-content: center;
    }
  }
  .el-input {
    height: 38px;
    input {
      height: 38px;
    }
  }
  .input-icon {
    height: 39px;
    width: 14px;
    margin-left: 2px;
  }
  .back-to-login-link {
    width: 100%;
    cursor: pointer;
    font-size: 14px;
    font-weight: bold;
    text-align: center;
    display: block;
    margin-top: 10px;
    &:hover {
      text-decoration: underline;
      color: #D71518;
    }
  }
  .forgot-password-link {
    width: 100%;
    cursor: pointer;
    font-size: 14px;
    font-weight: bold;
    text-align: right;
    margin-bottom: 15px;
    &:hover {
      text-decoration: underline;
      color: #D71518;
    }
  }
  .success-content {
    text-align: center;
    padding: 40px 0;
    .success-icon {
      font-size: 64px;
      color: #67C23A;
      margin-bottom: 20px;
    }
    .success-message {
      font-size: 20px;
      font-weight: bold;
      color: #303133;
      margin-bottom: 10px;
    }
    .success-tips {
      font-size: 14px;
      color: #909399;
    }
  }
}

@media screen and (max-width: 1024px) {
  .forgot-password {
    left: 0;
    width: 100vw;
  }
  .forgot-form {
    margin: 0 auto;
    width: 80vw;
    padding: 40px;
    .login-title {
      .logo-img {
        background-size: 100%;
        background-position: -60px;
      }
      .logo-title {
        font-size: 20px;
      }
    }
  }
}

.login-code {
  width: 35%;
  height: 38px;
  float: right;
}

.send-code-btn {
  width: 100%;
  height: 38px;
  padding: 0 8px;
  font-size: 13px;
}

/deep/ .el-form-item__content {
  display: flex;
}

/deep/ .el-button--primary:hover {
  background: rgba(0, 0, 0, 0.8);
  border-color: rgba(0, 0, 0, 0.8);
}

/deep/ .el-button--primary {
  background: #000;
  border-color: #000;
}

/deep/ .el-button--primary.is-disabled,
/deep/ .el-button--primary.is-disabled:hover {
  background: #C0C4CC;
  border-color: #C0C4CC;
}

/deep/ .el-select-dropdown__item.selected {
  color: #D71518;
}
</style>
